import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Chip,
  Card,
  CardMedia,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";
// import HomeIcon from "@mui/icons-material/Home";
// import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
// import ApartmentIcon from "@mui/icons-material/Apartment";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ConstructionIcon from "@mui/icons-material/Construction";
import BusinessIcon from "@mui/icons-material/Business";
import MapIcon from "@mui/icons-material/Map";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HotelIcon from "@mui/icons-material/Hotel";
import KingBedIcon from "@mui/icons-material/KingBed";
import SingleBedIcon from "@mui/icons-material/SingleBed";
import WeekendIcon from "@mui/icons-material/Weekend"; 
import SEO from "../../utils/SeoComponent";


// import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import { createTheme, ThemeProvider } from "@mui/material/styles";
const PropertyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // const theme = useTheme();
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    const fetchPropertyData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `https://panel.estaty.app/api/v1/getProperty`,
          {
            id: id, // sending ID in body
          },
          {
            headers: {
              "App-key": "3dc9efc076a8d36602ef445c924fa7cf",
            },
          }
        );
        setPropertyData(response.data.property);
        console.log(response.data.property);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching property data:", error);
        setError("Failed to load property data. Please try again later.");
        setLoading(false);
      }
    };

    if (id) {
      fetchPropertyData();
    }
  }, [id]);

  console.log("propertyData", propertyData);
  const theme = createTheme({
    palette: {
      primary: {
        main: "#d32f2f", // Red color
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  // Exit early if loading or error
  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress sx={{ color: "#C10E14" }} />
      </Container>
    );
  }

  if (error || !propertyData) {
    return (
      <Container sx={{ textAlign: "center", my: 8 }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || "Property not found"}
        </Typography>
        <Button variant="contained" component={Link} to="/" sx={{ mt: 2 }}>
          Return Home
        </Button>
      </Container>
    );
  }

  // Parse coordinates from the address string if needed
  const coordinates = propertyData.address
    ? propertyData.address.replace(/"/g, "").split(",")
    : [25.0, 55.0];
  const latitude = coordinates[0];
  const longitude = coordinates[1];

  // Parse description HTML
  const sanitizedDescription = DOMPurify.sanitize(propertyData.description);

  // Process property images
  const propertyImages = propertyData?.property_images || [];
  console.log("propertyImages", propertyImages);

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === propertyImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? propertyImages.length - 1 : prevIndex - 1
    );
  };

  const handleThumbnailClick = (index) => {
    console.log("index", index);

    setCurrentImageIndex(index);
  };

  // Extract property facilities for amenities section
  const amenities = propertyData?.property_facilities || [];

  return ( 
    <> 

     <SEO slug={"projectdetailpage"} />
        <Container
      maxWidth="lg"
      sx={{
        my: 4,
        paddingTop: {
          xs: "60px",
          sm: "80px",
          md: "100px",
          lg: "120px",
        },
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <MuiLink component={Link} to="/" color="inherit">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/projects" color="inherit">
          Properties
        </MuiLink>
        <Typography color="text.primary">{propertyData.title}</Typography>
      </Breadcrumbs>

      {/* Property Title */}
      <Typography
        variant="h3"
        component="h1"
        sx={{
          mb: 2,
          fontWeight: 700,
          color: "#C10E14",
        }}
      >
        {propertyData.title}
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          color: "text.secondary",
        }}
      >
        <LocationOnIcon sx={{ mr: 1 }} />
        <a
          style={{ color: "black" }}
          href={propertyData.address_text}
          target="_blank"
          rel="noopener noreferrer"
        >
          {propertyData.district?.name}, {propertyData.city?.name}
        </a>
      </Typography>

      <Grid container spacing={4}>
        {/* Images Section */}
        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{ mb: 4, overflow: "hidden", borderRadius: 2 }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Main Image */}
              <Box
                sx={{
                  width: "100%",
                  height: { xs: "250px", sm: "400px", md: "500px" },
                  position: "relative",
                  "&:hover .navigation": {
                    opacity: 1,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={propertyImages[currentImageIndex]?.image}
                  alt={`${propertyData.title} - Image ${currentImageIndex + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                {console.log("currentImageIndex", currentImageIndex)} 
                {console.log("")}

                {/* Image Caption */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "white",
                    p: 2,
                  }}
                >
                  <Typography variant="body1">
                    {propertyData.title} - {currentImageIndex + 1}/
                    {propertyImages?.length}
                  </Typography>
                </Box>

                {/* Navigation Arrows */}
                <Box
                  className="navigation"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    opacity: { xs: 1, md: 0 },
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.7)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                      ml: 1,
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>

                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.7)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                      mr: 1,
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Image Thumbnails */}
              {propertyImages?.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    p: 2,
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                  }}
                >
                  {propertyImages?.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: "100px",
                        height: "70px",
                        flexShrink: 0,
                        mr: 1,
                        border:
                          index === currentImageIndex
                            ? "3px solid #C10E14"
                            : "3px solid transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <CardMedia
                        component="img"
                        image={image?.image}
                        alt={`${propertyData?.title} - Thumbnail ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Description and Details */}
        <Grid item xs={12} md={8}>
          {/* Key Property Info */}
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderColor: "#e7e7e7",
              p: 3,
              mb: 4,
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                elevation: 6,
                transform: "translateY(-4px)",
                boxShadow: theme.shadows[6],
              },
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
              Property Overview
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Card
                  elevation={0}
                  sx={{
                    // borderRight: { sm: "1px solid #e0e0e0" },
                    height: "100%",
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <ApartmentIcon
                    sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {propertyData.property_type?.name || "Residential"}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <ConstructionIcon
                    sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {propertyData.property_status?.name || "Off Plan"}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <DateRangeIcon
                    sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Completion
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {propertyData.delivery_date || "10/2025"}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <BusinessIcon
                    sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Developer
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {propertyData.developer_company?.name ||
                      "Danube Properties"}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          <Divider
            sx={{
              my: 2.5,
              opacity: 0.7,
              "&::before, &::after": {
                borderColor: "#C10E14",
              },
            }}
          />

          {/* Property Description */}
          <Paper
            elevation={0.5}
            sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: "#fff1f1" }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              About{" "}
              <span style={{ color: "#C10E14" }}>{propertyData.title}</span>
            </Typography>
            <div
              style={{ backgroundColor: "#fff1f1" }}
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          </Paper>

          <Divider
            sx={{
              my: 2.5,
              opacity: 0.7,
              "&::before, &::after": {
                borderColor: "#C10E14",
              },
            }}
          />

          {/* {available units} */}
          <>
            {/* Main Title */}
            <Typography
              variant="h5"
              align="left"
              sx={{
                fontWeight: 700,
                mb: 4,
                mt: 2,
              }}
            >
              Available Units
            </Typography>

            {/* Residential & Commercial Units */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Residential Units Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "100%",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Residential Units
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {propertyData.residential_units}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total residential units available
                        </Typography>
                      </Box>
                      <ApartmentIcon sx={{ color: "#C10E14", fontSize: 40 }} />
                    </Box>

                    <Typography
                      variant="h2"
                      sx={{
                        color: "#C10E14",
                        my: 2,
                        fontWeight: 700,
                      }}
                    >
                      {propertyData.residential_units}
                    </Typography>

                    {/* <Typography variant="body2" color="text.secondary">
                      Last updated: Today
                    </Typography> */}
                  </Box>
                </Paper>
              </Grid>

              {/* Commercial Units Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "100%",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Commercial Units
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Total commercial units available
                        </Typography>
                      </Box>
                      <ApartmentIcon sx={{ color: "#C10E14", fontSize: 40 }} />
                    </Box>

                    <Typography
                      variant="h2"
                      sx={{
                        color: "#C10E14",
                        my: 2,
                        fontWeight: 700,
                      }}
                    >
                      {propertyData.commercial_units}
                    </Typography>

                    {/* <Typography variant="body2" color="text.secondary">
                      Last updated: Today
                    </Typography> */}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Unit Breakdown Section */}
            {/* <Paper
              elevation={1}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <Box sx={{ mt: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Unit Breakdown:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",

                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Studio
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        272
                      </Typography>
                      <ApartmentIcon sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",

                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        1 Bedroom
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        228
                      </Typography>
                      <SingleBedIcon sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",

                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        2 Bedrooms
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        73
                      </Typography>
                      <KingBedIcon sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        3 Bedrooms
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        17
                      </Typography>
                      <KingBedIcon sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper> */}
          </>

          {/* Amenities */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: { xs: 4, md: 0 },
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: 3,
              },
            }}
          >
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Amenities
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {amenities.map((amenity, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      transition: "transform 0.2s ease",
                      "&:hover": {
                        transform: "translateX(5px)",
                      },
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        color: "#C10E14",
                        mr: 1.5,
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.primary",
                        fontWeight: 500,
                      }}
                    >
                      {amenity?.facility?.name || `Amenity ${index + 1}`}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Price Info */}
          {/* <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              Price Information
            </Typography>
            <Box sx={{ textAlign: "center", my: 2 }}>
              <Typography
                variant="h4"
                sx={{ color: "#C10E14" }}
                fontWeight="bold"
              >
                AED {propertyData.low_price?.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Starting price
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold">
              Payment Plan:
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="body1">
                Down Payment: {propertyData.payment_minimum_down_payment}%
              </Typography>
              <Typography variant="body1">
                Monthly Payment: {propertyData.payment_plan}% per month
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Down payment amount: AED{" "}
                {propertyData.downPayment?.toLocaleString()}
              </Typography>
            </Box>
          </Paper> */}
          <ThemeProvider theme={theme}>
            <Paper
              elevation={2}
              sx={{ maxWidth: 400, borderRadius: "8px", mb: 4 }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  bgcolor: "#fff1f1",
                  p: 2,
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  Price Information
                </Typography>
              </Box>

              {/* Price Section */}
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Starting price
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{ my: 1 }}
                >
                  AED {propertyData.low_price?.toLocaleString()}
                </Typography>
              </Box>

              {/* Payment Plan Section */}
              <Box sx={{ px: 3, pb: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  sx={{ mb: 2 }}
                >
                  Payment Plan
                </Typography>

                <Grid container spacing={2}>
                  {/* Down Payment */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            Down Payment
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {propertyData.payment_minimum_down_payment}% of
                            total amount
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ mt: 1 }}
                          >
                            AED {propertyData.downPayment.toLocaleString()}
                          </Typography>
                        </Box>
                        <IconButton size="small">
                          <InfoIcon fontSize="small" color="action" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Monthly Payment */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            Monthly Payment
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {propertyData.payment_plan}% per month
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ mt: 1 }}
                          >
                            AED {propertyData.downPayment?.toLocaleString()}
                          </Typography>
                        </Box>
                        <IconButton size="small">
                          <InfoIcon fontSize="small" color="action" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </ThemeProvider>

          {/* Location Info */}
          <Paper
            elevation={2}
            sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: "#f9fafb" }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              Location
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <MapIcon sx={{ mr: 1, color: "#C10E14" }} />
              <Typography variant="body1">
                {propertyData.district?.name}, {propertyData.city?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                height: "200px",
                mb: 2,
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                allowFullScreen
                title="Location Map"
              ></iframe>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LocationOnIcon />}
                href={propertyData.address_text}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#C10E14",
                  borderColor: "#C10E14",
                  "&:hover": { borderColor: "#a60c11" },
                }}
              >
                View on Maps
              </Button>
            </Box>
          </Paper>

          {/* CTA */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "#C10E14",
              color: "primary.contrastText",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Interested in {propertyData.title}?
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Get in touch with our agents for more information or to arrange a
              viewing.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                fullWidth
                sx={{
                  bgcolor: "white",
                  color: "#C10E14",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                Request Details
              </Button>
              <Button
                variant="outlined"
                fullWidth
                component={Link}
                to="/contact-us"
                sx={{
                  color: "white",
                  borderColor: "white",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Contact Agent
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}></Grid>
      </Grid>
    </Container>
    </>

  );
};

export default PropertyDetailPage;
